// Conversations page (Phase 5.3). Two-level UX:
//   • A table of the most recent 100 threads — agent · last activity · cost ·
//     message count · widget-vs-SDK origin.
//   • Click a row to expand inline; the trace is fetched on demand
//     (per-conversation queryKey) and rendered as message cards with their
//     associated ai_call_log row threaded underneath.
//
// Why inline expansion (not a side drawer / second route): operators usually
// want to scan + open one or two threads, not deep-link to them. A two-pane
// flow is more clicks for the common case. If the trace gets bigger than this
// page can hold gracefully we'll graduate it to its own route.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Code2, MessagesSquare } from 'lucide-react';
import type {
  ConversationSummary,
  ConversationTrace,
  ConversationTraceCallLog,
  ConversationTraceMessage,
} from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { ApiError } from '../../lib/api-client.js';
import { formatDateTime } from '../../lib/format-date.js';
import {
  conversationsApi,
  CONVERSATIONS_QUERY_KEY,
  conversationTraceQueryKey,
} from '../../lib/api/conversations.js';
import { Badge } from '../../components/ui/badge.js';
import { Card, CardContent } from '../../components/ui/card.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { cn } from '../../lib/utils.js';

export function ConversationsListPage() {
  const api = useApiClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: CONVERSATIONS_QUERY_KEY,
    queryFn: () => conversationsApi.list(api),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Conversations"
        description="Last 100 chat threads. Expand any row to inspect the message trace and the provider call that produced each turn."
      />

      {list.isLoading && <TableSkeleton />}
      {list.isError && (
        <ErrorRetry
          title="Could not load conversations"
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      )}
      {list.data?.conversations.length === 0 && (
        <EmptyState
          icon={MessagesSquare}
          title="No conversations yet"
          description="Once a widget or SDK caller hits an agent, the conversation will land here within a few seconds."
        />
      )}
      {list.data && list.data.conversations.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" aria-hidden />
                  <TableHead>Conversation</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Origin</TableHead>
                  <TableHead className="text-right">Messages</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>Last activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.conversations.map((c) => (
                  <ConversationRow
                    key={c.id}
                    conversation={c}
                    expanded={expandedId === c.id}
                    onToggle={() => { setExpandedId(expandedId === c.id ? null : c.id); }}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TableSkeleton() {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="grid grid-cols-6 items-center gap-4 px-4 py-3">
              <Skeleton className="col-span-2 h-4" />
              <Skeleton className="h-4" />
              <Skeleton className="h-5 w-12 rounded-full" />
              <Skeleton className="h-4" />
              <Skeleton className="h-4" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface ConversationRowProps {
  conversation: ConversationSummary;
  expanded: boolean;
  onToggle: () => void;
}

function ConversationRow({ conversation, expanded, onToggle }: ConversationRowProps) {
  const Icon = expanded ? ChevronDown : ChevronRight;
  return (
    <>
      <TableRow
        onClick={onToggle}
        className="cursor-pointer"
        aria-expanded={expanded}
      >
        <TableCell className="w-8">
          <button
            type="button"
            aria-label={expanded ? 'Collapse trace' : 'Expand trace'}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <Icon className="h-4 w-4" aria-hidden />
          </button>
        </TableCell>
        <TableCell>
          <div className="font-medium">
            {conversation.title ?? <span className="text-muted-foreground">Untitled</span>}
          </div>
          <div className="font-mono text-[11px] text-muted-foreground">{conversation.id}</div>
        </TableCell>
        <TableCell className="text-sm">
          {conversation.agentName ?? (
            <span className="italic text-muted-foreground">deleted</span>
          )}
        </TableCell>
        <TableCell>
          <Badge variant="muted" className="font-mono text-[11px]">
            {conversation.externalSessionId ? 'widget' : 'sdk'}
          </Badge>
        </TableCell>
        <TableCell className="text-right tabular-nums">{conversation.messageCount}</TableCell>
        <TableCell className="text-right font-mono tabular-nums">
          ${conversation.totalCostUsd.toFixed(4)}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {formatDateTime(conversation.updatedAt)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={7} className="p-4">
            <ConversationTracePanel id={conversation.id} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ConversationTracePanel({ id }: { id: string }) {
  const api = useApiClient();
  const trace = useQuery({
    queryKey: conversationTraceQueryKey(id),
    queryFn: () => conversationsApi.trace(api, id),
  });

  if (trace.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (trace.isError) {
    const status = trace.error instanceof ApiError ? trace.error.status : undefined;
    const code = trace.error instanceof ApiError ? trace.error.code : undefined;
    return (
      <p role="alert" className="text-sm text-destructive">
        Could not load trace
        {status !== undefined ? ` (HTTP ${String(status)})` : ''}
        {code ? ` · ${code}` : ''}
      </p>
    );
  }
  if (!trace.data) return <></>;

  return <TraceBody trace={trace.data} />;
}

function TraceBody({ trace }: { trace: ConversationTrace }) {
  // Group call logs by messageId. Logs without a messageId are listed at the
  // bottom under "Other provider calls" — they exist (older rows or aborted
  // turns) but don't slot under a particular message.
  const callsByMessage = new Map<string, ConversationTraceCallLog[]>();
  const orphanCalls: ConversationTraceCallLog[] = [];
  for (const call of trace.callLogs) {
    if (call.messageId) {
      const existing = callsByMessage.get(call.messageId) ?? [];
      existing.push(call);
      callsByMessage.set(call.messageId, existing);
    } else {
      orphanCalls.push(call);
    }
  }

  return (
    <div className="space-y-3">
      {trace.messages.length === 0 && (
        <p className="text-sm text-muted-foreground">No messages recorded for this conversation.</p>
      )}
      {trace.messages.map((m) => (
        <MessageBlock
          key={m.id}
          message={m}
          calls={callsByMessage.get(m.id) ?? []}
        />
      ))}
      {orphanCalls.length > 0 && (
        <div className="rounded-md border border-dashed p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Other provider calls
          </p>
          <div className="space-y-2">
            {orphanCalls.map((c) => (
              <CallLogRow key={c.id} call={c} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ROLE_VARIANT: Record<ConversationTraceMessage['role'], 'warning' | 'default' | 'success' | 'secondary'> = {
  system: 'warning',
  user: 'default',
  assistant: 'success',
  tool: 'secondary',
};

function MessageBlock({
  message,
  calls,
}: {
  message: ConversationTraceMessage;
  calls: ConversationTraceCallLog[];
}) {
  return (
    <div className="rounded-md border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <Badge variant={ROLE_VARIANT[message.role]} className="capitalize">
          #{message.sequence} · {message.role}
        </Badge>
        {message.finishReason && (
          <span className="text-xs text-muted-foreground">finish: {message.finishReason}</span>
        )}
      </div>
      <pre className="whitespace-pre-wrap break-words text-sm">{message.content || '(empty)'}</pre>
      {message.toolCalls && message.toolCalls.length > 0 && (
        <div className="mt-2 space-y-1">
          {message.toolCalls.map((tc) => (
            <div
              key={tc.id}
              className="flex items-start gap-2 rounded bg-muted px-2 py-1 font-mono text-xs"
            >
              <Code2 className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
              <span className="break-all">
                {tc.name}({JSON.stringify(tc.arguments)})
              </span>
            </div>
          ))}
        </div>
      )}
      {calls.length > 0 && (
        <div className="mt-2 space-y-1 border-t pt-2">
          {calls.map((c) => (
            <CallLogRow key={c.id} call={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function CallLogRow({ call }: { call: ConversationTraceCallLog }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-xs',
        call.errorClass ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      <span className="font-mono">
        {call.provider}/{call.model}
      </span>
      <span className="tabular-nums">${call.costUsd.toFixed(6)}</span>
      <span className="tabular-nums">{call.latencyMs}ms</span>
      <span className="tabular-nums">
        {call.inputTokens}→{call.outputTokens} tok
      </span>
      {call.fallbackTriggered && (
        <Badge variant="warning" className="text-[10px]">fallback</Badge>
      )}
      {call.retries > 0 && <span>retries: {call.retries}</span>}
      {call.errorClass && <span className="font-mono">err: {call.errorClass}</span>}
    </div>
  );
}
